// src/admin/pages/CreateInvoicePage.js
import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Trash2, 
  Save,
  X,
  AlertCircle,
  CheckCircle,
  Loader2,
  User,
  Copy,
  ExternalLink
} from 'lucide-react';
import { supabase } from '../../config';
import stripeService from '../../services/stripeService';
import CurrencyService from '../../services/currencyService';
import { getUserDisplayName } from '../../services/utilityService';

const CreateInvoicePage = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [customerId, setCustomerId] = useState(null);
  const [createdInvoice, setCreatedInvoice] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [customerType, setCustomerType] = useState('registered'); // 'registered' or 'external'
  const [externalCustomer, setExternalCustomer] = useState({
    name: '',
    email: ''
  });
  
  // Invoice form data
  const [invoiceData, setInvoiceData] = useState({
    description: '',
    line_items: [{ description: '', quantity: 1, unit_amount: 0, currency: 'USD' }],
    days_until_due: 30,
    collection_method: 'send_invoice', // 'send_invoice' or 'charge_automatically'
    auto_advance: false, // Always create as draft - finalize later from invoice list
    send_invoice: false,
    currency: 'USD' // Default currency for the invoice
  });
  
  // Get available currencies
  const availableCurrencies = CurrencyService.SUPPORTED_CURRENCIES.filter(c => c.isActive);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, first_name, last_name, email, username')
        .order('first_name')
        .limit(100);

      if (error) throw error;
      setUsers(data || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = async (user) => {
    setSelectedUser(user);
    setError('');
    setMessage('');
    
    // Get customer ID for this user
    try {
      const cId = await stripeService.getCustomerId(user.id);
      if (cId) {
        setCustomerId(cId);
      } else {
        // Customer will be created when invoice is created
        setCustomerId(null);
        setMessage('Customer will be created automatically when invoice is created.');
      }
    } catch (err) {
      console.error('Error getting customer ID:', err);
    }
  };

  const handleLineItemChange = (index, field, value) => {
    const newLineItems = [...invoiceData.line_items];
    if (field === 'quantity') {
      newLineItems[index] = {
        ...newLineItems[index],
        [field]: parseInt(value) || 1
      };
    } else if (field === 'unit_amount') {
      // Ensure unit_amount is always an integer (cents)
      newLineItems[index] = {
        ...newLineItems[index],
        [field]: Math.round(parseFloat(value) || 0)
      };
    } else {
      newLineItems[index] = {
        ...newLineItems[index],
        [field]: value
      };
    }
    setInvoiceData({ ...invoiceData, line_items: newLineItems });
  };

  const addLineItem = () => {
    setInvoiceData({
      ...invoiceData,
      line_items: [...invoiceData.line_items, { description: '', quantity: 1, unit_amount: 0, currency: invoiceData.currency }]
    });
  };

  const removeLineItem = (index) => {
    if (invoiceData.line_items.length > 1) {
      const newLineItems = invoiceData.line_items.filter((_, i) => i !== index);
      setInvoiceData({ ...invoiceData, line_items: newLineItems });
    }
  };

  const calculateTotal = () => {
    return invoiceData.line_items.reduce((sum, item) => {
      return sum + (item.unit_amount * item.quantity);
    }, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (customerType === 'registered' && !selectedUser) {
      setError('Please select a user');
      return;
    }

    if (customerType === 'external') {
      if (!externalCustomer.name || !externalCustomer.email) {
        setError('Please enter customer name and email');
        return;
      }
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(externalCustomer.email)) {
        setError('Please enter a valid email address');
        return;
      }
    }

    // Validate line items before sending
    const invalidItems = invoiceData.line_items.filter(item => 
      !item.description || !item.description.trim() || item.unit_amount <= 0
    );
    
    if (invalidItems.length > 0) {
      const itemNumbers = invalidItems.map((_, idx) => {
        const actualIdx = invoiceData.line_items.indexOf(invalidItems[idx]);
        return actualIdx + 1;
      }).join(', ');
      
      setError(`Line item${invalidItems.length > 1 ? 's' : ''} ${itemNumbers}: Please fill in description and amount (must be > $0.00)`);
      return;
    }
    
    // Log what we're about to send
    console.log('📤 About to create invoice with:', {
      customerType,
      customerId: customerType === 'registered' ? customerId : 'external',
      line_items_count: invoiceData.line_items.length,
      line_items: invoiceData.line_items.map(item => ({
        description: item.description,
        quantity: item.quantity,
        unit_amount: item.unit_amount,
        unit_amount_dollars: `$${(item.unit_amount / 100).toFixed(2)}`,
        currency: item.currency
      })),
      auto_advance: invoiceData.auto_advance
    });

    setSaving(true);
    setError('');
    setMessage('');

    try {
      // Ensure all line items have the correct currency
      const lineItemsWithCurrency = invoiceData.line_items.map(item => ({
        ...item,
        currency: item.currency || invoiceData.currency || 'USD'
      }));
      
      const invoicePayload = {
        description: invoiceData.description,
        line_items: lineItemsWithCurrency,
        days_until_due: invoiceData.days_until_due,
        collection_method: invoiceData.collection_method,
        auto_advance: false, // Always create as draft
        send_invoice: false, // Never send on creation
        currency: invoiceData.currency || 'USD'
      };

      if (customerType === 'registered') {
        invoicePayload.user_id = selectedUser.id;
        invoicePayload.customer_id = customerId; // Will be created if null
      } else {
        // External customer - create without user_id
        invoicePayload.customer_name = externalCustomer.name;
        invoicePayload.customer_email = externalCustomer.email;
      }

      const response = await stripeService.createInvoice(invoicePayload);

      console.log('📥 Invoice creation response received:', response);

      if (response && response.success) {
        setCreatedInvoice(response.invoice);
        setShowInvoiceModal(true);
        setMessage('');
        setError('');
      } else {
        console.warn('⚠️ Invoice creation response missing success flag:', response);
        setError('Invoice creation response was invalid. Please check the console for details.');
      }
    } catch (err) {
      // Display error with more details
      const errorMessage = err.message || 'Failed to create invoice';
      console.error('Invoice creation error details:', err);
      setError(errorMessage);
      
      // If error contains debug info, log it
      if (err.debug) {
        console.error('Debug info:', err.debug);
      }
    } finally {
      setSaving(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const search = searchTerm.toLowerCase();
    const displayName = getUserDisplayName(user, '').toLowerCase();
    return (
      displayName.includes(search) ||
      user.email?.toLowerCase().includes(search) ||
      user.username?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="w-full max-w-full space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Create Invoice</h1>
          <p className="text-gray-600 mt-2">Create and send invoices for one-time payments</p>
        </div>
      </div>

      {error && (
        <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <div className="flex items-center mb-2">
            <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
            <span className="font-medium">Error creating invoice</span>
          </div>
          <div className="ml-6 text-sm whitespace-pre-wrap">{error}</div>
          <div className="ml-6 mt-2 text-xs text-red-600">
            💡 Check browser console (F12 → Console tab) and Network tab for detailed error information
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Customer Type Selection */}
        <div className="bg-white rounded-lg border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Customer Type</h2>
          <div className="flex gap-4 mb-4">
            <label className="flex items-center">
              <input
                type="radio"
                value="registered"
                checked={customerType === 'registered'}
                onChange={(e) => {
                  setCustomerType(e.target.value);
                  setSelectedUser(null);
                  setCustomerId(null);
                  setExternalCustomer({ name: '', email: '' });
                }}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Registered User</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value="external"
                checked={customerType === 'external'}
                onChange={(e) => {
                  setCustomerType(e.target.value);
                  setSelectedUser(null);
                  setCustomerId(null);
                }}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">External Customer (Not Registered)</span>
            </label>
          </div>

          {/* User Selection (for registered users) */}
          {customerType === 'registered' && (
            <>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Select Customer</h2>
          
          <div className="mb-4">
            <input
              type="text"
              placeholder="Search users by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {selectedUser ? (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center">
                <User className="h-5 w-5 text-blue-600 mr-3" />
                <div>
                  <div className="font-medium text-gray-900">{getUserDisplayName(selectedUser, selectedUser.username)}</div>
                  <div className="text-sm text-gray-600">{selectedUser.email}</div>
                  {customerId && (
                    <div className="text-xs text-gray-500 mt-1">Customer ID: {customerId}</div>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSelectedUser(null);
                  setCustomerId(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          ) : (
            <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
              {loading ? (
                <div className="p-8 text-center">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto text-blue-600" />
                </div>
              ) : filteredUsers.length > 0 ? (
                filteredUsers.map(user => (
                  <div
                    key={user.id}
                    onClick={() => handleUserSelect(user)}
                    className="p-4 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                  >
                    <div className="font-medium text-gray-900">{getUserDisplayName(user, user.username)}</div>
                    <div className="text-sm text-gray-600">{user.email}</div>
                  </div>
                ))
              ) : (
                <div className="p-8 text-center text-gray-500">No users found</div>
              )}
            </div>
          )}
            </>
          )}

          {/* External Customer Form (for non-registered customers) */}
          {customerType === 'external' && (
            <>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Customer Details</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Customer Name *
                  </label>
                  <input
                    type="text"
                    value={externalCustomer.name}
                    onChange={(e) => setExternalCustomer({ ...externalCustomer, name: e.target.value })}
                    placeholder="Enter customer name"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Customer Email *
                  </label>
                  <input
                    type="email"
                    value={externalCustomer.email}
                    onChange={(e) => setExternalCustomer({ ...externalCustomer, email: e.target.value })}
                    placeholder="Enter customer email"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <p className="text-sm text-gray-600">
                  A Stripe customer will be created automatically when the invoice is created.
                </p>
              </div>
            </>
          )}
        </div>

        {/* Invoice Details */}
        <div className="bg-white rounded-lg border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Invoice Details</h2>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Currency *
            </label>
            <select
              value={invoiceData.currency}
              onChange={(e) => {
                const newCurrency = e.target.value;
                setInvoiceData({ 
                  ...invoiceData, 
                  currency: newCurrency,
                  line_items: invoiceData.line_items.map(item => ({
                    ...item,
                    currency: newCurrency
                  }))
                });
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              {availableCurrencies.map(currency => (
                <option key={currency.code} value={currency.code}>
                  {currency.flag} {currency.code} - {currency.name}
                </option>
              ))}
            </select>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description (optional)
            </label>
            <input
              type="text"
              value={invoiceData.description}
              onChange={(e) => setInvoiceData({ ...invoiceData, description: e.target.value })}
              placeholder="Invoice description..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Line Items */}
          <div className="mb-4">
            <div className="flex justify-between items-center mb-3">
              <label className="block text-sm font-medium text-gray-700">Line Items</label>
              <button
                type="button"
                onClick={addLineItem}
                className="flex items-center text-sm text-blue-600 hover:text-blue-700"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Item
              </button>
            </div>

            <div className="space-y-3">
              {invoiceData.line_items.map((item, index) => (
                <div key={index} className="flex gap-3 items-start p-3 border border-gray-200 rounded-lg">
                  <div className="flex-1">
                    <input
                      type="text"
                      placeholder="Item description"
                      value={item.description}
                      onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2"
                      required
                    />
                    <div className="flex gap-2">
                      <input
                        type="number"
                        placeholder="Quantity"
                        value={item.quantity}
                        onChange={(e) => handleLineItemChange(index, 'quantity', e.target.value)}
                        min="1"
                        step="1"
                        className="w-24 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                      <div className="flex-1 flex items-center gap-1">
                        <span className="text-gray-600">$</span>
                        <input
                          type="number"
                          placeholder="0"
                          value={item.unit_amount > 0 ? Math.floor(item.unit_amount / 100) : ''}
                          onChange={(e) => {
                            const dollars = parseInt(e.target.value) || 0;
                            const cents = item.unit_amount % 100;
                            const totalCents = dollars * 100 + cents;
                            handleLineItemChange(index, 'unit_amount', totalCents);
                          }}
                          min="0"
                          step="1"
                          className="w-20 px-2 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                        <span className="text-gray-600">.</span>
                        <input
                          type="number"
                          placeholder="00"
                          value={item.unit_amount > 0 ? String(item.unit_amount % 100).padStart(2, '0') : ''}
                          onChange={(e) => {
                            const centsInput = e.target.value;
                            // Only allow 0-99
                            let cents = parseInt(centsInput) || 0;
                            if (cents > 99) cents = 99;
                            if (cents < 0) cents = 0;
                            const dollars = Math.floor(item.unit_amount / 100);
                            const totalCents = dollars * 100 + cents;
                            handleLineItemChange(index, 'unit_amount', totalCents);
                          }}
                          min="0"
                          max="99"
                          step="1"
                          className="w-16 px-2 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                    </div>
                  </div>
                  {invoiceData.line_items.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeLineItem(index)}
                      className="text-red-600 hover:text-red-700 p-2"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200 flex justify-end">
              <div className="text-right">
                <div className="text-sm text-gray-600">Total</div>
                <div className="text-2xl font-bold text-gray-900">
                  ${(calculateTotal() / 100).toFixed(2)}
                </div>
              </div>
            </div>
          </div>

          {/* Invoice Settings */}
          <div className="space-y-4 pt-4 border-t border-gray-200">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Days Until Due
              </label>
              <input
                type="number"
                value={invoiceData.days_until_due}
                onChange={(e) => setInvoiceData({ ...invoiceData, days_until_due: parseInt(e.target.value) || 30 })}
                min="0"
                className="w-32 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                ℹ️ <strong>Note:</strong> Invoices are created as drafts. You can finalize and send them later from the One-off Invoices page.
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => window.history.back()}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || (customerType === 'registered' && !selectedUser) || (customerType === 'external' && (!externalCustomer.name || !externalCustomer.email))}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating Invoice...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Create Invoice
              </>
            )}
          </button>
        </div>
      </form>

      {/* Full-screen Creating Overlay */}
      {saving && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 flex flex-col items-center shadow-xl">
            <Loader2 className="h-12 w-12 animate-spin text-blue-600 mb-4" />
            <p className="text-lg font-medium text-gray-900">Creating Invoice...</p>
            <p className="text-sm text-gray-500 mt-2">Please wait while we create your invoice</p>
          </div>
        </div>
      )}

      {/* Invoice Created Modal */}
      {showInvoiceModal && createdInvoice && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 bg-green-50 rounded-t-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <CheckCircle className="h-6 w-6 text-green-600 mr-3" />
                  <h2 className="text-xl font-semibold text-gray-900">Invoice Created Successfully!</h2>
                </div>
                <button
                  onClick={() => setShowInvoiceModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              {/* Invoice Info Grid */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Invoice ID</div>
                  <div className="text-sm font-mono text-gray-900 break-all">{createdInvoice.id}</div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Status</div>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 capitalize">
                    {createdInvoice.status || 'draft'}
                  </span>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Total Amount</div>
                  <div className="text-2xl font-bold text-gray-900">
                    ${((createdInvoice.total || createdInvoice.amount_due || 0) / 100).toFixed(2)}
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Customer</div>
                  <div className="text-sm text-gray-900">
                    {customerType === 'registered' 
                      ? getUserDisplayName(selectedUser || {}, 'N/A')
                      : (externalCustomer.name || externalCustomer.email || 'N/A')}
                  </div>
                </div>
              </div>

              {/* Payment URL */}
              {createdInvoice.hosted_invoice_url ? (
                <div className="mb-6">
                  <div className="text-sm font-semibold text-gray-700 mb-2">Invoice Payment URL:</div>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      readOnly
                      value={createdInvoice.hosted_invoice_url}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm bg-gray-50 font-mono"
                      onClick={(e) => e.target.select()}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(createdInvoice.hosted_invoice_url);
                        setMessage('URL copied!');
                        setTimeout(() => setMessage(''), 2000);
                      }}
                      className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center text-sm"
                      title="Copy URL"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                    <a
                      href={createdInvoice.hosted_invoice_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center text-sm"
                      title="Open in new tab"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              ) : createdInvoice.status === 'draft' && (
                <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Draft Invoice:</strong> Payment URL will be available after finalization.
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    Go to AdminHQ → Payments → Drafts tab to edit and finalize this invoice.
                  </p>
                </div>
              )}

              {/* Line Items */}
              {createdInvoice.line_items && createdInvoice.line_items.length > 0 && (
                <div className="mb-6">
                  <div className="text-sm font-semibold text-gray-700 mb-2">Line Items:</div>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {createdInvoice.line_items.map((item, idx) => {
                          const quantity = item.quantity || 1;
                          const unitAmount = item.unit_amount || (item.amount ? item.amount / quantity : 0);
                          const total = unitAmount * quantity;
                          return (
                            <tr key={idx}>
                              <td className="px-4 py-3 text-gray-900">{item.description || 'N/A'}</td>
                              <td className="px-4 py-3 text-right text-gray-900">{quantity}</td>
                              <td className="px-4 py-3 text-right text-gray-900">${(unitAmount / 100).toFixed(2)}</td>
                              <td className="px-4 py-3 text-right text-gray-900 font-medium">${(total / 100).toFixed(2)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-gray-50">
                        <tr>
                          <td colSpan="3" className="px-4 py-3 text-right font-semibold text-gray-900">Total:</td>
                          <td className="px-4 py-3 text-right font-bold text-gray-900">
                            ${((createdInvoice.total || createdInvoice.amount_due || 0) / 100).toFixed(2)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              {message && (
                <div className="mb-4 text-sm text-green-600 flex items-center">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  {message}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg flex justify-between">
              <button
                type="button"
                onClick={() => {
                  setShowInvoiceModal(false);
                  setCreatedInvoice(null);
                  setMessage('');
                  setError('');
                  setInvoiceData({
                    description: '',
                    line_items: [{ description: '', quantity: 1, unit_amount: 0, currency: invoiceData.currency || 'USD' }],
                    days_until_due: 30,
                    collection_method: 'send_invoice',
                    auto_advance: false,
                    send_invoice: false,
                    currency: invoiceData.currency || 'USD'
                  });
                  setSelectedUser(null);
                  setCustomerId(null);
                  setExternalCustomer({ name: '', email: '' });
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm"
              >
                Create Another Invoice
              </button>
              <div className="flex gap-2">
                <a
                  href="/adminhq/payments?tab=drafts"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm inline-flex items-center"
                >
                  View All Drafts
                </a>
                <button
                  type="button"
                  onClick={() => setShowInvoiceModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateInvoicePage;

